import { useMemo, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AlertTriangle, Calendar as CalendarIcon, CheckCircle2, X } from 'lucide-react';
import { DAY_NAMES } from './utils';

/**
 * Two-click assignment dialog — opened by clicking an empty mini-bar segment.
 *
 * Lists every route whose cell at `date` is empty. Operator picks one and we
 * call `onPick(route, applyAllWeek)`. When the "Aynı rotayı tüm haftaya
 * uygula" toggle is on, each row also shows how many of the 7 days the
 * batch would actually fill (skipping leave days, already-filled cells).
 */
export function QuickAssignDialog({
  quickAssign,
  routeGroups,
  assignByCell,
  leaveByEmpDate,
  weekDates = [],
  onClose,
  onPick,
}) {
  const { emp, date } = quickAssign;
  const [applyAll, setApplyAll] = useState(false);

  let dayName = '';
  try { dayName = DAY_NAMES[(new Date(date).getDay() + 6) % 7]; } catch { /* ignore */ }
  const isOnLeaveThisDay = leaveByEmpDate.has(`${emp.id}|${date}`);

  const availableRoutes = useMemo(() => {
    const out = [];
    for (const c of routeGroups) {
      for (const dep of c.depots) {
        for (const r of dep.routes) {
          if (assignByCell.has(`${r.id}|${date}`)) continue;
          let weekFreeCount = 0;
          if (applyAll && weekDates.length === 7) {
            for (const d of weekDates) {
              if (leaveByEmpDate.has(`${emp.id}|${d}`)) continue;
              if (assignByCell.has(`${r.id}|${d}`)) continue;
              weekFreeCount += 1;
            }
          }
          out.push({ ...r, _company_name: c.company_name, _depot_name: dep.depot_name, _weekFreeCount: weekFreeCount });
        }
      }
    }
    return out;
  }, [routeGroups, assignByCell, leaveByEmpDate, emp.id, date, applyAll, weekDates]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-lg w-full p-0 overflow-hidden flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()} data-testid="planning-quick-assign-dialog">
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-emerald-500" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold truncate">{emp.full_name}</h3>
            <p className="text-[11px] text-slate-500">{dayName} · {date}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-red-500" data-testid="planning-quick-assign-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {isOnLeaveThisDay ? (
          <div className="p-5 text-center">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-amber-500" />
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Bu çalışan bu gün izinli — atama yapılamaz.
            </p>
          </div>
        ) : availableRoutes.length === 0 ? (
          <div className="p-5 text-center">
            <p className="text-sm text-slate-500">Bu güne uygun (boş) rota yok.</p>
          </div>
        ) : (
          <>
            <label className="flex items-center gap-2 px-5 py-2 text-[11px] text-slate-600 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700 cursor-pointer select-none bg-slate-50/60 dark:bg-slate-800/40">
              <input
                type="checkbox"
                checked={applyAll}
                onChange={(e) => setApplyAll(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-slate-300 text-emerald-500 focus:ring-emerald-400"
                data-testid="planning-quick-assign-apply-all"
              />
              <span className="font-medium">Aynı rotayı tüm haftaya uygula</span>
              <span className="text-[10px] text-slate-400">(boş + izinsiz günlere)</span>
            </label>
            <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
              <p className="text-[10px] uppercase tracking-wide text-slate-400 px-2 mb-1">
                {applyAll ? 'Rotaya tıkla — uygun günlere otomatik dağıtılacak' : `Boş rota seç (${availableRoutes.length})`}
              </p>
              {availableRoutes.map((r) => (
                <button
                  key={r.id}
                  onClick={() => onPick(r, applyAll)}
                  className="w-full text-left px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 hover:border-emerald-300 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 transition-colors flex items-center gap-2"
                  data-testid={`planning-quick-assign-route-${r.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-semibold">{r.route_code}</span>
                      {r.owner_firm_name && (
                        <span className="text-[9px] px-1 py-0 rounded bg-indigo-100 text-indigo-700 font-bold uppercase">
                          {r.owner_firm_name}
                        </span>
                      )}
                      {applyAll && (
                        <span
                          className={`text-[9px] px-1.5 py-0 rounded font-bold ${
                            r._weekFreeCount > 0
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                              : 'bg-slate-100 text-slate-500 border border-slate-200'
                          }`}
                          title="Bu rota için bu hafta atanabilecek gün sayısı"
                        >
                          {r._weekFreeCount}/7 gün
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-500 truncate">
                      {r._company_name}{r._depot_name ? ` · ${r._depot_name}` : ''}
                      {r.region_name ? ` · ${r.region_name}` : ''}
                      {r.default_start_time ? ` · ${r.default_start_time}` : ''}
                    </p>
                  </div>
                  <CheckCircle2 className="h-4 w-4 text-slate-300" />
                </button>
              ))}
            </div>
          </>
        )}

        <div className="px-5 py-2 border-t border-slate-100 dark:border-slate-700 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onClose}>Kapat</Button>
        </div>
      </Card>
    </div>
  );
}
